import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, MessageSquarePlus, Clock, Send, AlertTriangle, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { mccPost, mccDownloadUrl } from './mccApi';
import { Avatar } from './mccUi';
import { humanDateTime } from './mccConstants';

const fmtTs = (s) => {
  if (s === null || s === undefined || Number.isNaN(s)) return '';
  const t = Math.max(0, Math.floor(s));
  const m = Math.floor(t / 60);
  const sec = t % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

/**
 * Video review player — cinematic dark shell with timestamped feedback.
 *
 * Props:
 *   version: the version doc (id, file_id, label, feedback[], content_type)
 *   canGiveFeedback: boolean
 *   onFeedback: async(newFeedbackEntry) — parent can update its version state
 */
export default function MccVideoReview({ version, canGiveFeedback = true, onFeedback }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [text, setText] = useState('');
  const [kind, setKind] = useState('change_request');
  const [sending, setSending] = useState(false);

  useEffect(() => { setT(0); setPlaying(false); setText(''); setKind('change_request'); }, [version?.id]);

  const isVideo = (version?.content_type || '').startsWith('video/');
  const isImage = (version?.content_type || '').startsWith('image/');
  const url = version?.file_id ? mccDownloadUrl(version.file_id) : null;

  const onTimeUpdate = () => setT(videoRef.current?.currentTime || 0);

  const jumpTo = (secs) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = secs;
    videoRef.current.play();
    setPlaying(true);
  };

  const submit = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      const res = await mccPost('/marketing/versions/feedback', {
        version_id: version.id,
        timestamp_seconds: isVideo ? t : null,
        comment: body,
        kind,
      });
      setText('');
      if (onFeedback) onFeedback(res);
      toast.success('Feedback added');
    } catch (e) {
      toast.error(e.message || 'Feedback failed');
    } finally {
      setSending(false);
    }
  };

  const feedback = [...(version?.feedback || [])].sort((a, b) => {
    // Timestamped first, sorted by time; then general (by created_at asc)
    const ta = a.timestamp_seconds; const tb = b.timestamp_seconds;
    if (ta != null && tb != null) return ta - tb;
    if (ta != null) return -1;
    if (tb != null) return 1;
    return (a.at || '').localeCompare(b.at || '');
  });

  const feedbackIconOf = (k) => (
    k === 'change_request' ? <AlertTriangle className="w-3 h-3" /> :
    k === 'approval' ? <Check className="w-3 h-3" /> :
    <MessageSquarePlus className="w-3 h-3" />
  );

  const feedbackTone = (k) => (
    k === 'change_request' ? { bg: '#fee2e2', ink: '#991b1b' } :
    k === 'approval' ? { bg: '#dcfce7', ink: '#166534' } :
    k === 'question' ? { bg: '#fef3c7', ink: '#78350f' } :
    { bg: '#dbeafe', ink: '#1e40af' }
  );

  return (
    <div className="rounded-2xl overflow-hidden bg-slate-950 text-slate-100 border border-slate-800">
      <div className="p-4 sm:p-6 border-b border-slate-800 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold text-indigo-400 uppercase tracking-[0.14em]">Review · Version {version?.version_number}</p>
          <h3 className="text-lg font-bold truncate">{version?.label || version?.file_name}</h3>
          <p className="text-xs text-slate-400 truncate">Uploaded by {version?.uploaded_by_name} · {humanDateTime(version?.uploaded_at)}</p>
        </div>
        {version?.approval_status ? (
          <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded bg-slate-800 text-slate-200">
            {String(version.approval_status).replace(/_/g, ' ')}
          </span>
        ) : null}
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-0">
        {/* Media panel */}
        <div className="p-4 sm:p-6 bg-black flex flex-col items-center justify-start">
          {isVideo && url ? (
            <>
              <video
                ref={videoRef}
                src={url}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onTimeUpdate={onTimeUpdate}
                onLoadedMetadata={onTimeUpdate}
                className="w-full max-h-[520px] rounded-lg bg-black"
                controls
                data-testid={`mcc-review-video-${version?.id}`}
              />
              <div className="mt-3 flex items-center gap-2 w-full">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => (playing ? videoRef.current?.pause() : videoRef.current?.play())}
                  data-testid="mcc-review-toggle"
                >
                  {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </Button>
                <span className="text-xs tabular-nums text-slate-300">{fmtTs(t)}</span>
                <span className="text-[10px] text-slate-500 ml-auto">Click a timestamp on the right to jump →</span>
              </div>
            </>
          ) : isImage && url ? (
            <img src={url} alt={version?.file_name} className="w-full max-h-[520px] object-contain rounded-lg" />
          ) : (
            <div className="text-slate-400 text-sm py-8 text-center">
              <p className="font-medium">Preview not supported for this file type.</p>
              {url ? <a className="underline text-indigo-400" href={url} rel="noreferrer" target="_blank">Download {version?.file_name}</a> : null}
            </div>
          )}
        </div>

        {/* Feedback panel */}
        <div className="border-t lg:border-t-0 lg:border-l border-slate-800 flex flex-col max-h-[620px]">
          <div className="p-4 border-b border-slate-800 flex-shrink-0">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.14em]">Feedback thread</p>
            <p className="text-xs text-slate-500 mt-1">{feedback.length} entry{feedback.length === 1 ? '' : 's'}</p>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[220px]">
            {feedback.length === 0 ? (
              <p className="text-center text-xs text-slate-500 py-8">No feedback yet — leave the first note below.</p>
            ) : feedback.map((f) => {
              const tone = feedbackTone(f.kind);
              return (
                <div
                  key={f.id}
                  className="rounded-lg border border-slate-800 bg-slate-900 p-3 hover:bg-slate-900/80 transition"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {f.timestamp_seconds != null ? (
                      <button
                        onClick={() => jumpTo(f.timestamp_seconds)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold text-xs bg-indigo-500 text-white hover:bg-indigo-700 transition"
                        data-testid={`mcc-feedback-jump-${f.id}`}
                      >
                        <Clock className="w-3 h-3" />{fmtTs(f.timestamp_seconds)}
                      </button>
                    ) : (
                      <span className="text-[10px] uppercase text-slate-500 font-semibold">General</span>
                    )}
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase"
                      style={{ background: tone.bg, color: tone.ink }}
                    >
                      {feedbackIconOf(f.kind)} {(f.kind || 'note').replace(/_/g, ' ')}
                    </span>
                    <span className="ml-auto text-[10px] text-slate-500">{humanDateTime(f.at)}</span>
                  </div>
                  <p className="text-sm text-slate-200 mt-2 whitespace-pre-wrap">{f.comment}</p>
                  <div className="flex items-center gap-1.5 mt-2">
                    <Avatar name={f.by_name} size={16} />
                    <span className="text-[11px] text-slate-400">{f.by_name}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {canGiveFeedback && (
            <div className="border-t border-slate-800 p-3 flex-shrink-0">
              <div className="flex items-center gap-2 mb-2">
                {isVideo && (
                  <span className="text-[10px] uppercase font-bold text-indigo-400 tracking-wider">
                    <Clock className="w-3 h-3 inline mr-1" />@ {fmtTs(t)}
                  </span>
                )}
                <Select value={kind} onValueChange={setKind}>
                  <SelectTrigger className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-200 flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="change_request">Change request</SelectItem>
                    <SelectItem value="question">Question</SelectItem>
                    <SelectItem value="approval">Approval note</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={isVideo ? `Leave feedback at ${fmtTs(t)}…` : 'Leave feedback…'}
                rows={3}
                className="text-sm bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500 resize-none"
                data-testid="mcc-review-feedback-input"
              />
              <div className="flex justify-end mt-2">
                <Button
                  onClick={submit}
                  disabled={sending || !text.trim()}
                  size="sm"
                  data-testid="mcc-review-feedback-submit"
                  className="bg-indigo-500 hover:bg-indigo-700 text-white"
                >
                  <Send className="w-3 h-3 mr-1" /> Send feedback
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
